// baseline-illustrations.js - "How is this measured?" setup diagrams for each
// baseline exercise (Module 2, V6_DESIGN §7.9). Native inline SVG (cones,
// distances, gates) so they scale + theme + ship in code - no image files.
// Geometry transcribed from the KHC Dragons Testing Protocol (2026-06-11).
//
// Reachable from the tester test screen, the player session view, and the
// director wizard. Director-reviewable copy lives in DATA (loose item L7).

import { CONFIG } from './config.js';
import { exerciseByKey } from './baseline-data.js';
import { t } from './strings.js';
import { UTILS } from './utils.js';

export const BASELINE_ILLUSTRATIONS = (() => {

  // Cone / accent colours (match the protocol's colour coding).
  const C_GREEN = '#2FB457', C_ORANGE = '#E8902A', C_RED = '#D23B2B';
  const GOLD = '#A78F58', INK = '#F5F4F0', MUTE = '#9AA0AA', LINE = '#6B7180';

  const cone  = (x, y, c = C_ORANGE) =>
    `<polygon points="${x},${y - 7} ${x - 4.5},${y + 4} ${x + 4.5},${y + 4}" fill="${c}"/><rect x="${x - 5}" y="${y + 4}" width="10" height="1.6" fill="${c}"/>`;
  const ball  = (x, y, r = 3.2) => `<circle cx="${x}" cy="${y}" r="${r}" fill="${INK}"/>`;
  const label = (x, y, s, opts = '') => `<text x="${x}" y="${y}" fill="${MUTE}" font-size="9" font-family="sans-serif" ${opts}>${s}</text>`;
  const ptlbl = (x, y, s) => `<text x="${x}" y="${y}" fill="${INK}" font-size="9" font-weight="700" text-anchor="middle" font-family="sans-serif">${s}</text>`;

  // Shared gate line (6 cones → 5 gates: 0/1/3/1/0, centre 2 m). Used by the
  // three distribution tests; `dist` + `time` + `dynamic` vary per test.
  function gateSvg({ distance, dynamic }) {
    const xs = [55, 95, 135, 175, 215, 255];        // 6 cones
    const cols = [C_GREEN, C_ORANGE, C_RED, C_RED, C_ORANGE, C_GREEN];
    const gy = 40;
    const cones = xs.map((x, i) => cone(x, gy, cols[i])).join('');
    const gates = [['0', 75], ['1', 115], ['3', 155], ['1', 195], ['0', 235]]
      .map(([p, x]) => ptlbl(x, gy - 12, p)).join('');
    const centreArrow = `<line x1="135" y1="54" x2="175" y2="54" stroke="${MUTE}" stroke-width="1"/><text x="155" y="64" fill="${MUTE}" font-size="8" text-anchor="middle" font-family="sans-serif">2 m</text>`;
    const shootY = 210;
    const balls = Array.from({ length: 8 }, (_, i) => ball(120 + i * 9, shootY)).join('');
    const distArrow = `<line x1="40" y1="${gy}" x2="40" y2="${shootY}" stroke="${LINE}" stroke-width="1"/><text x="34" y="${(gy + shootY) / 2}" fill="${MUTE}" font-size="9" text-anchor="middle" font-family="sans-serif" transform="rotate(-90 34 ${(gy + shootY) / 2})">${distance} m</text>`;
    const moveGate = dynamic
      ? `${cone(120, 165, C_ORANGE)}${cone(240, 165, C_ORANGE)}<line x1="128" y1="165" x2="232" y2="165" stroke="${MUTE}" stroke-width="1" stroke-dasharray="3 3"/><text x="180" y="178" fill="${MUTE}" font-size="8" text-anchor="middle" font-family="sans-serif">5 m</text>`
      : '';
    return `<svg viewBox="0 0 300 230" width="100%" xmlns="http://www.w3.org/2000/svg" role="img">
      ${distArrow}${cones}${gates}${centreArrow}${moveGate}${balls}
      ${label(116, shootY + 16, dynamic ? '4 push + 4 shot' : '8 balls')}
    </svg>`;
  }

  function illinoisSvg() {
    const x0 = 105, x1 = 205, y0 = 22, y1 = 208;
    const corners = [cone(x0, y0), cone(x1, y0), cone(x0, y1), cone(x1, y1)].join('');
    const cx = (x0 + x1) / 2;
    const centre = [0, 1, 2, 3].map(i => cone(cx, y0 + 34 + i * ((y1 - y0 - 60) / 3))).join('');
    const rect = `<rect x="${x0}" y="${y0}" width="${x1 - x0}" height="${y1 - y0}" fill="none" stroke="${LINE}" stroke-width="1" stroke-dasharray="2 3"/>`;
    return `<svg viewBox="0 0 300 230" width="100%" xmlns="http://www.w3.org/2000/svg" role="img">
      ${rect}${corners}${centre}${ball(x0 - 9, y1)}
      <text x="${cx}" y="14" fill="${MUTE}" font-size="9" text-anchor="middle" font-family="sans-serif">5 m</text>
      <text x="${x1 + 14}" y="${(y0 + y1) / 2}" fill="${MUTE}" font-size="9" text-anchor="middle" font-family="sans-serif" transform="rotate(90 ${x1 + 14} ${(y0 + y1) / 2})">10 m</text>
      ${label(x0 - 30, y1 + 14, 'Start')}
    </svg>`;
  }

  function tTestSvg() {
    const bx = 150, by = 195, tyy = 55, cxL = 70, cxR = 230;
    const stem = `<line x1="${bx}" y1="${by}" x2="${bx}" y2="${tyy}" stroke="${GOLD}" stroke-width="1" stroke-dasharray="3 3"/>`;
    const cross = `<line x1="${cxL}" y1="${tyy}" x2="${cxR}" y2="${tyy}" stroke="${GOLD}" stroke-width="1" stroke-dasharray="3 3"/>`;
    return `<svg viewBox="0 0 300 230" width="100%" xmlns="http://www.w3.org/2000/svg" role="img">
      ${stem}${cross}
      ${cone(bx, by)}${cone(bx, tyy, C_RED)}${cone(cxL, tyy)}${cone(cxR, tyy)}
      ${ball(bx + 11, by)}
      <text x="${bx + 8}" y="${(by + tyy) / 2}" fill="${MUTE}" font-size="9" font-family="sans-serif">10 m</text>
      <text x="${(cxL + bx) / 2}" y="${tyy - 10}" fill="${MUTE}" font-size="9" text-anchor="middle" font-family="sans-serif">5 m</text>
      <text x="${(cxR + bx) / 2}" y="${tyy - 10}" fill="${MUTE}" font-size="9" text-anchor="middle" font-family="sans-serif">5 m</text>
      ${label(bx - 14, by + 16, 'Start (A)')}
    </svg>`;
  }

  function dribbleSvg() {
    const lx = 80, rx = 220, y = 80;
    return `<svg viewBox="0 0 300 140" width="100%" xmlns="http://www.w3.org/2000/svg" role="img">
      ${cone(lx, y)}${cone(rx, y)}${ball(lx, y + 16)}
      <line x1="${lx + 8}" y1="${y - 14}" x2="${rx - 8}" y2="${y - 14}" stroke="${GOLD}" stroke-width="1"/>
      <polygon points="${lx + 8},${y - 14} ${lx + 14},${y - 17} ${lx + 14},${y - 11}" fill="${GOLD}"/>
      <polygon points="${rx - 8},${y - 14} ${rx - 14},${y - 17} ${rx - 14},${y - 11}" fill="${GOLD}"/>
      ${label(105, y + 34, '1 rep = full out-and-back')}
    </svg>`;
  }

  // 3 km run - a simple lap-circuit icon with start/finish + direction.
  function run3kmSvg() {
    return `<svg viewBox="0 0 300 150" width="100%" xmlns="http://www.w3.org/2000/svg" role="img">
      <ellipse cx="150" cy="75" rx="115" ry="52" fill="none" stroke="${LINE}" stroke-width="2" stroke-dasharray="2 4"/>
      <ellipse cx="150" cy="75" rx="92" ry="34" fill="none" stroke="${LINE}" stroke-width="1" stroke-dasharray="2 4" opacity="0.5"/>
      <line x1="150" y1="123" x2="150" y2="113" stroke="${GOLD}" stroke-width="3"/>
      ${ptlbl(150, 140, 'Start / finish')}
      <polygon points="60,30 52,26 52,34" fill="${C_GREEN}"/>
      <text x="150" y="79" fill="${MUTE}" font-size="11" text-anchor="middle" font-family="sans-serif">3 km</text>
    </svg>`;
  }

  // Australian shuttle - the 0/25/50/75/100 m ladder, up then down.
  function ausShuttleSvg() {
    const xs = [25, 90, 155, 220, 285];           // 0 / 25 / 50 / 75 / 100 m
    const dists = ['0', '25', '50', '75', '100'];
    const y = 78;
    const cones = xs.map((x, i) => cone(x, y, i === 0 ? C_GREEN : C_ORANGE)).join('');
    const dlabels = xs.map((x, i) => `<text x="${x}" y="${y + 20}" fill="${MUTE}" font-size="9" text-anchor="middle" font-family="sans-serif">${dists[i]}</text>`).join('');
    const lane = `<line x1="25" y1="${y}" x2="285" y2="${y}" stroke="${LINE}" stroke-width="1" stroke-dasharray="2 3"/>`;
    const upArrow = `<path d="M30 ${y - 16} H280" stroke="${GOLD}" stroke-width="1.5" fill="none"/><polygon points="280,${y - 16} 273,${y - 19} 273,${y - 13}" fill="${GOLD}"/><text x="155" y="${y - 20}" fill="${INK}" font-size="8.5" text-anchor="middle" font-family="sans-serif">up: 25, 50, 75, 100 (and back each)</text>`;
    const dnArrow = `<path d="M280 ${y + 34} H30" stroke="${C_RED}" stroke-width="1.5" fill="none"/><polygon points="30,${y + 34} 37,${y + 31} 37,${y + 37}" fill="${C_RED}"/><text x="155" y="${y + 45}" fill="${INK}" font-size="8.5" text-anchor="middle" font-family="sans-serif">down: 100, 75, 50, 25 (and back each)</text>`;
    return `<svg viewBox="0 0 300 150" width="100%" xmlns="http://www.w3.org/2000/svg" role="img">
      ${upArrow}${lane}${cones}${dlabels}${dnArrow}
      <text x="150" y="18" fill="${MUTE}" font-size="9" text-anchor="middle" font-family="sans-serif">metres from the start cone</text>
    </svg>`;
  }

  // Per-exercise content. Distances are from the protocol; durations/ball caps
  // come from CONFIG so they never drift from the timer behaviour.
  const ex = (k) => exerciseByKey(k) || {};
  // Duration can live on the exercise (gates) or on its FitnessTimer protocol
  // (the dribble) - resolve both so the metric never drifts from the timer.
  const secs = (k) => {
    const e = ex(k);
    const ms = e.durationMs || (e.protocol && CONFIG.fitness.protocols[e.protocol]?.durationMs) || 0;
    return Math.round(ms / 1000);
  };

  const DATA = {
    illinois_no_ball: {
      svg: illinoisSvg,
      metrics: () => [{ l: 'Course', v: '10 m × 5 m' }, { l: 'Better', v: 'Fastest time' }],
      steps: [
        'Set a 10 m × 5 m rectangle with a cone at each corner.',
        'Place 4 cones in a line down the centre, evenly spaced.',
        'Player lies face-down (or stands) at the start cone, bottom-left.',
        'On "go", start the clock; sprint the Illinois pattern: up the side, weave the centre cones and back, up the side, finish.',
        'Stop the clock as they cross the finish. Save the time.',
      ],
      checklist: ['Cones square and measured', 'Surface dry and clear', 'Player warmed up', 'One clean demo run shown'],
    },
    illinois_with_ball: {
      svg: illinoisSvg,
      metrics: () => [{ l: 'Course', v: '10 m × 5 m' }, { l: 'Better', v: 'Fastest time' }],
      steps: [
        'Same 10 m × 5 m course as the no-ball Illinois.',
        'Player keeps a ball on the stick for the entire run.',
        'On "go", start the clock; run the Illinois pattern while dribbling.',
        'A lost ball must be recovered before continuing - the clock keeps running.',
        'Stop the clock at the finish. Save the time.',
      ],
      checklist: ['Cones square and measured', 'Ball + stick ready', 'Player warmed up', 'One clean demo run shown'],
    },
    t_test_no_ball: {
      svg: tTestSvg,
      metrics: () => [{ l: 'Stem', v: '10 m' }, { l: 'Arms', v: '5 m each' }, { l: 'Better', v: 'Fastest time' }],
      steps: [
        'Cone A (start), cone B 10 m straight ahead, cones C and D 5 m left and right of B.',
        'On "go", start the clock; sprint A → B.',
        'Side-shuffle B → C, then C → D, then D → B (facing forward throughout).',
        'Backpedal B → A. Stop the clock at A. Save the time.',
      ],
      checklist: ['T measured and square', 'Player faces forward when shuffling', 'Surface dry', 'One clean demo run shown'],
    },
    t_test_with_ball: {
      svg: tTestSvg,
      metrics: () => [{ l: 'Stem', v: '10 m' }, { l: 'Arms', v: '5 m each' }, { l: 'Better', v: 'Fastest time' }],
      steps: [
        'Same T as the no-ball version (A start, B 10 m, C/D 5 m each side).',
        'Player keeps the ball on the stick throughout.',
        'On "go", start the clock; A → B → C → D → B → A while controlling the ball.',
        'Stop the clock back at A. Save the time.',
      ],
      checklist: ['T measured and square', 'Ball + stick ready', 'Surface dry', 'One clean demo run shown'],
    },
    // Added 2026-07-23 when the key turned up in results without a catalogue
    // entry. Protocol below is the one the club has been running; confirm the
    // cone distance with the trainers and adjust if it differs.
    yard_stick_dribble: {
      svg: dribbleSvg,
      metrics: () => [{ l: 'Scoring', v: 'Rep count' }, { l: 'Better', v: 'Most reps' }],
      steps: [
        'Set the cones out at the standard yard-stick spacing.',
        'Player starts with the ball at the first cone.',
        'On "go", the player dribbles the course, one rep per completed pass.',
        'Count every clean rep; a lost ball does not count.',
        'Save the total rep count.',
      ],
      checklist: ['Same cone spacing as last time', 'Ball + stick ready', 'Player warmed up', 'Counter at zero before start'],
    },
    dribble_40s: {
      svg: dribbleSvg,
      metrics: () => [{ l: 'Time', v: `${secs('dribble_40s')} s` }, { l: 'Better', v: 'Most reps' }],
      steps: [
        'Set two cones a fixed distance apart (use the same distance every time).',
        'Player starts with the ball at the first cone.',
        'On "go", start the 40-second timer.',
        'Player dribbles out-and-back between the cones as many times as possible.',
        'One rep = a full out-and-back. Tap the rep button for each completed rep.',
        'At 0 s the timer stops automatically. Save the rep count.',
      ],
      checklist: ['Same cone distance as last time', 'Ball + stick ready', 'Player warmed up', 'Counter at zero before start'],
    },
    static_push: {
      svg: () => gateSvg({ distance: 30, dynamic: false }),
      metrics: () => [{ l: 'Distance', v: '30 m' }, { l: 'Time', v: `${secs('static_push')} s` }, { l: 'Balls', v: `${ex('static_push').ballCap}` }],
      steps: [
        'Set the gate line: 6 cones making 5 gates - centre gate 2 m wide.',
        'Scoring by gate: centre = 3 pts, the two beside it = 1 pt, the outer two = 0 pts.',
        'Place 8 balls at the pushing spot, 30 m from the gates.',
        'On "go", start the 10-second timer; player pushes balls at the gates.',
        'Tap 3 / 1 / 0 for each ball as it passes the line. Save the total.',
      ],
      checklist: ['Gates measured (2 m centre)', '8 balls at the spot', '30 m distance marked', 'Scorer ready on 3/1/0'],
    },
    static_shot: {
      svg: () => gateSvg({ distance: 40, dynamic: false }),
      metrics: () => [{ l: 'Distance', v: '40 m' }, { l: 'Time', v: `${secs('static_shot')} s` }, { l: 'Balls', v: `${ex('static_shot').ballCap}` }],
      steps: [
        'Same gate line as the push test - centre 3 pts, sides 1 pt, outer 0 pts.',
        'Place 8 balls at the shooting spot, 40 m from the gates.',
        'On "go", start the 15-second timer; player shoots at the gates.',
        'Tap 3 / 1 / 0 for each ball. Save the total.',
      ],
      checklist: ['Gates measured (2 m centre)', '8 balls at the spot', '40 m distance marked', 'Scorer ready on 3/1/0'],
    },
    dynamic_dist: {
      svg: () => gateSvg({ distance: 40, dynamic: true }),
      metrics: () => [{ l: 'Distance', v: '40 m' }, { l: 'Time', v: `${secs('dynamic_dist')} s` }, { l: 'Balls', v: '4 push + 4 shot' }],
      steps: [
        'Gate line 40 m away (centre 3 pts, sides 1 pt, outer 0 pts).',
        'A 5 m movement gate sits in front of the player; 8 balls at the spot.',
        'On "go", start the 50-second timer.',
        'First 4 balls are PUSHES, the next 4 are SHOTS (the screen flips PUSH → SHOT for you).',
        'Tap 3 / 1 / 0 for each ball. Push and shot scores are saved separately.',
      ],
      checklist: ['Gates + 5 m movement gate set', '40 m distance marked', '8 balls (4 push, 4 shot)', 'Scorer ready on 3/1/0'],
    },
    run_3km: {
      svg: run3kmSvg,
      metrics: () => [{ l: 'Distance', v: '3 km' }, { l: 'Better', v: 'Fastest time' }],
      steps: [
        'Mark a measured 3 km course or count laps of a known circuit.',
        'Start the clock on "go".',
        'Player runs the full 3 km at their best sustainable pace.',
        'Stop the clock as they cross the finish. Save the time.',
      ],
      checklist: ['Course distance confirmed', 'Lap count known', 'Player warmed up', 'Water available'],
    },
    aus_shuttle: {
      svg: ausShuttleSvg,
      metrics: () => [{ l: 'One set', v: '1000 m' }, { l: 'Turns', v: '~16' }, { l: 'Better', v: 'Fastest total' }],
      steps: [
        'Set 5 cones from a baseline at 0, 25, 50, 75 and 100 m.',
        'On "go", run to 25 m and back, 50 m and back, 75 m and back, 100 m and back.',
        'Then come back down: 100 m and back, 75 m and back, 50 m and back, 25 m and back. That is one set (1000 m).',
        'Rest 2 minutes, then repeat for the planned number of sets.',
        'Time each set (tap the lap button at the end of each one) so the fatigue drop-off between sets is captured.',
      ],
      checklist: ['Cones at 0/25/50/75/100 m', 'Distances measured', 'Stopwatch ready (lap per set)', '2 min rest timed between sets'],
    },
  };

  function show(exerciseKey) {
    const e = exerciseByKey(exerciseKey);
    const d = DATA[exerciseKey];
    if (!e || !d) return;
    const esc = UTILS.escapeHtml;
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.innerHTML = `
      <div class="modal" style="max-width:460px; max-height:90vh; overflow-y:auto;">
        <div class="modal__title">${esc(e.name)}</div>
        <div class="modal__body">
          <div class="bl-illu">${typeof d.svg === 'function' ? d.svg() : d.svg}</div>
          <div class="bl-illu-metrics">
            ${d.metrics().map(m => `<span class="bl-illu-metric"><b>${esc(m.v)}</b> ${esc(m.l)}</span>`).join('')}
          </div>
          <div class="gate__label" style="margin-top:12px;">${esc(t('blillu.setup'))}</div>
          <ol class="bl-illu-steps">${d.steps.map(s => `<li>${esc(s)}</li>`).join('')}</ol>
          <div class="gate__label" style="margin-top:10px;">${esc(t('blillu.checklist'))}</div>
          <ul class="bl-illu-check">${d.checklist.map(c => `<li>${esc(c)}</li>`).join('')}</ul>
        </div>
        <div class="modal__buttons"><button class="modal-btn modal-btn--primary" data-action="close">${esc(t('blillu.close'))}</button></div>
      </div>`;
    document.body.appendChild(overlay);
    overlay.querySelector('[data-action="close"]').addEventListener('click', () => overlay.remove());
  }

  return { show, DATA };

})();
